/* eslint-disable max-len */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import injectSheet from 'react-jss';
import { Typography } from 'antd';
import { classesPropType } from '../prop-types';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL, PRIVACY_EMAIL } from '../../config';
import Content from './Content';
import TabLeft from './TabLeft';

const { Paragraph, Text, Title } = Typography;

const styles = {
  summary: {
    marginBottom: '20px',
  },
  emph: {
    fontStyle: 'italic',
  },
};

function PrivacyPolicy({ classes }) {
  const { t } = useTranslation();
  const bold = useCallback(text => <Text strong>{text}</Text>, []);
  const emph = useCallback(text => <Text className={classes.emph}>{text}</Text>, [classes.emph]);
  return (
    <Content title={t('privacyPolicy.title')}>
      <div className={classes.summary}>
        <Title level={2}>Summary:</Title>
        <li>We don&apos;t use your data to show you ads.</li>
        <li>We only store the data that Cupcake needs to function as a secure and feature-rich service.</li>
        <li>You are in control of who sees your data.</li>
      </div>

      <Title level={2}>1. Introduction</Title>
      <Paragraph>
        This Privacy Policy sets out how we, Cupcake, use and protect your personal data that you provide to us, or that is otherwise obtained or generated by us, in connection with your use of our cloud-based messaging services (the {bold('“Services”')}). For the purposes of this Privacy Policy, {bold("'we'")}, {bold("'us'")} and {bold("'our'")} refers to Cupcake, and {bold("'you'")} refers to you, the user of the Services.
      </Paragraph>

      <TabLeft>
        <Title level={3}>1.1 Privacy Principles</Title>
        <Paragraph>
          Cupcake has three principles when it comes to collecting and processing private data:
        </Paragraph>
        <ul>
          <li>We don&apos;t use your data to show you ads.</li>
          <li>We only store the data that Cupcake needs to function as a secure and feature-rich messaging service.</li>
          <li>You are in control of who sees your data</li>
        </ul>

        <Title level={3}>1.2 Terms of Service</Title>
        <Paragraph>
          This Privacy Policy forms part of our Terms of Service, which describes the terms under which you use our Services and which are available at <a href={TERMS_OF_SERVICE_URL}>{TERMS_OF_SERVICE_URL}</a>. This Privacy Policy should therefore be read in conjunction with those terms.
        </Paragraph>

        <Title level={3}>1.3 Table of Contents</Title>
        <Paragraph>This Privacy Policy explains the following:</Paragraph>
        <ul>
          <li>the legal basis for processing your personal data;</li>
          <li>what personal data we may collect from you;</li>
          <li>how we keep your personal data safe;</li>
          <li>what we may use your personal data for;</li>
          <li>who your personal data may be shared with; and</li>
          <li>your rights regarding your personal data.</li>
        </ul>
      </TabLeft>

      <Title level={2}>2. Legal Ground for Processing Your Personal Data</Title>
      <Paragraph>
        We process your personal data on the ground that such processing is necessary to further our legitimate interests (including: {emph('(1)')} providing effective and innovative Services to our users; and {emph('(2)')} to detect, prevent or otherwise address fraud or security issues in respect of our provision of Services), unless those interests are overridden by your interest or fundamental rights and freedoms that require protections of personal data.
      </Paragraph>

      <Title level={2}>3. What Personal Data We Use</Title>
      <TabLeft>
        <Title level={3}>3.1 Basic Account Data</Title>
        <Paragraph>
          Cupcake is a communication service. You provide your mobile number and basic account data (which includes first and last name, profile picture and birthday) to create a Cupcake account.
        </Paragraph>
        <Paragraph>
          To give you control on how your contacts and other people to reach you and recognize who you are, the first and last name you choose, your profile pictures, and your birthday on Cupcake are only visible if you grant them access.
        </Paragraph>
        <Paragraph>
          We do {bold('not')} require your first and last name to be your real name. Note that users who have you in their contacts as friend will see you by the name you saved, so it’s important you choose one that is recognizable for your friends.
        </Paragraph>

        <Title level={3}>3.2 Your data</Title>
        <TabLeft>
          <Title level={4}>3.2.1 Cloud data</Title>
          <Paragraph>
            Cupcake is a cloud service. We store messages, photos, videos and documents you sent to our servers so that you can access your data from any of your devices anytime without having to rely on third-party backups. All data is stored in a secure way.
          </Paragraph>

          <Title level={4}>3.2.2 Phone Number and Contacts</Title>
          <Paragraph>
            Cupcake uses phone numbers as unique identifiers so that it is easy for you to switch from SMS and other messaging apps and retain your social graph. We ask your permission before syncing your contacts and we will only share your data with other people if you explicitly grant them permission.
          </Paragraph>
          <Paragraph>
            We store your up-to-date contacts with birthday information in order to notify you of any of your contacts’s birthdays  and to properly display names in notifications. We only need the phone number, name (first and last) and birthday for this to work and store no other data about your contacts.
          </Paragraph>
        </TabLeft>
      </TabLeft>

      <Title level={2}>4. Keeping Your Personal Data Safe</Title>
      <TabLeft>
        <Title level={3}>4.1 Retention</Title>
        <Paragraph>
          Unless stated otherwise in this Privacy Policy, the personal data that you provide us will only be stored for as long as it is necessary for us to fulfill our obligations in respect of the provision of the Services.
        </Paragraph>
      </TabLeft>

      <Title level={2}>5. Processing Your Personal Data</Title>
      <TabLeft>
        <Title level={3}>5.1 Our Services</Title>
        <Paragraph>
          Cupcake is a cloud service. We will process your data to deliver your cloud data history, including messages, media and files, to any devices of your choosing without a need for you to use third-party backups or cloud storage.
        </Paragraph>
        <Title level={3}>5.2 Spam and Abuse</Title>
        <Paragraph>
          To prevent phishing, spam and other kinds of abuse and violations of Cupcake’s Terms of Service, our moderators may check messages that were reported to them by their recipients. If a spam report on a message you sent is confirmed by our moderators, your account may be limited from contacting strangers – temporarily or permanently. We may also use automated algorithms to analyze messages in cloud chats to stop spam and phishing.
        </Paragraph>
        <Title level={3}>5.3 Cross-Device Functionality</Title>
        <Paragraph>
          We may also store some aggregated metadata to create Cupcake features that work across all your devices.
        </Paragraph>
        <Title level={3}>5.4 Advanced features</Title>
        <Paragraph>
          We may use some aggregated data about how you use Cupcake to build useful features.
        </Paragraph>
        <Title level={3}>5.5 No Ads</Title>
        <Paragraph>
          Unlike other services, we don&apos;t use your data for ad targeting or other commercial purposes. Cupcake only stores the information it needs to function as a secure and feature-rich cloud service.
        </Paragraph>
      </TabLeft>

      <Title level={2}>6. Who Your Personal Data May Be Shared With</Title>
      <TabLeft>
        <Title level={3}>6.1 Other Cupcake Users</Title>
        <Paragraph>
          Other users of our Services with whom you choose to communicate with and share certain information, who may be located outside the EEA. Note that by entering into the Terms of Service and choosing to communicate with such other users of Cupcake, you are instructing us to transfer your personal data, on your behalf, to those users in accordance with this Privacy Policy. We employ all appropriate technical and organizational measures (including encryption of your personal data) to ensure a level of security for your personal data that is appropriate to the risk.
        </Paragraph>
        <Title level={3}>6.2 Law Enforcement Authorities</Title>
        <Paragraph>
          If Cupcake receives a court order that confirms you&apos;re a terror suspect, we may disclose your IP address and phone number to the relevant authorities. So far, this has never happened.
        </Paragraph>
      </TabLeft>

      <Title level={2}>7. Your Rights Regarding the Personal Data You Provide to Us</Title>
      <TabLeft>
        <Title level={3}>7.1 Your Rights</Title>
        <Paragraph>
          Under applicable data protection legislation, in certain circumstances, you have rights concerning your personal data. You have a right to: {emph('(1)')} request a copy of all your personal data that we store and to transmit that copy to another data controller; {emph('(2)')} delete or amend your personal data; {emph('(3)')} restrict, or object to, the processing of your personal data; {emph('(4)')} correct any inaccurate or incomplete personal data we hold on you; and {emph('(5)')} lodge a complaint with national data protection authorities regarding our processing of your personal data.
        </Paragraph>
        <Title level={3}>7.2 Exercising Your Rights</Title>
        <Paragraph>
          If you wish to exercise any of these rights, kindly contact us.
        </Paragraph>
      </TabLeft>

      <Title level={2}>8. Exercising Your Rights</Title>
      <TabLeft>
        <Title level={3}>8.1 Accounts</Title>
        <Paragraph>
          If you would like to delete your account, you can do this on the settings page. Deleting your account removes all messages, media, contacts and every other piece of data you store in the Cupcake cloud. This action must be confirmed via your Cupcake account and cannot be undone.
        </Paragraph>
      </TabLeft>

      <Title level={2}>9. Changes to this Privacy Policy</Title>
      <Paragraph>
        We will review and may update this Privacy Policy from time to time. Any changes to this Privacy Policy will become effective when we post the revised Privacy Policy on this page <a href={PRIVACY_POLICY_URL}>{PRIVACY_POLICY_URL}</a> Please check our website frequently to see any updates or changes to our Privacy Policy, a summary of which we will set out below.
      </Paragraph>
      <Paragraph>
        Important changes made to this Privacy Policy will be notified to you via Cupcake.
      </Paragraph>

      <Title level={2}>10. Questions and concerns</Title>
      <Paragraph>
        If you have any questions about privacy and our data policies, please contact <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>
      </Paragraph>
    </Content>
  );
}

PrivacyPolicy.propTypes = {
  classes: classesPropType.isRequired,
};

export default injectSheet(styles)(PrivacyPolicy);
